import { useScrollToFocus } from '@capturi/react-utils'
import {
  Box,
  Fade,
  HStack,
  Text,
  VStack,
  Wrap,
  chakra,
  useToast,
} from '@chakra-ui/react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useStore } from '@tanstack/react-store'
import Markdown, { RuleType } from 'markdown-to-jsx'
import { type FC, type RefObject, useState } from 'react'

import { ThumbsButtons } from '../../../components/ThumbsButtons'
import { useCaptureEvent } from '../../../utils/analytics'
import { MagicBox } from '../../components/MagicBox'
import { sessionStore } from '../../sessionStore'
import { KnowledgeBaseLink } from './KnowledgeBaseLink'
import { AnswerSkeleton, LinksSkeleton } from './SkeletonComponents'
import type { ChatResponse } from './useAskCopilot'

type Props = {
  answer: ChatResponse['answers'][number]
  answerIdx: number
  customer: string
  showThumbs: boolean
  containerRef?: RefObject<HTMLElement | null>
}

const ALLOWED_RULES: ReadonlySet<RuleType> = new Set([
  RuleType.breakLine,
  RuleType.orderedList,
  RuleType.paragraph,
  RuleType.text,
  RuleType.textBolded,
  RuleType.textEmphasized,
  RuleType.unorderedList,
])

export const AnswerItem: FC<Props> = ({
  answer,
  answerIdx,
  customer,
  showThumbs,
  containerRef,
}) => {
  const { organizationUid, sessionUid } = useStore(sessionStore)
  const captureEvent = useCaptureEvent()
  const toast = useToast()
  const [reviewed, setReviewed] = useState(false)
  const scrollToFocus = useScrollToFocus(containerRef)

  return (
    <VStack alignItems="center" spacing={0} ref={scrollToFocus}>
      <Box height="17px" width="1px" bg="greys.300" mt={2} />
      <Box data-testid={`answer-${answerIdx}`} mt={2} width="100%">
        <MagicBox>
          <Box p="4">
            <VStack spacing="3" align="stretch">
              <HStack justifyContent="space-between" gap={1}>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color="greys.500"
                  textTransform="uppercase"
                >
                  {answer.question}
                </Text>
                <Fade in={!reviewed && showThumbs}>
                  <HStack
                    gap="1"
                    justify="flex-end"
                    w="100%"
                    alignItems="flex-start"
                  >
                    <ThumbsButtons
                      onUp={() => {
                        captureEvent('handover_recommendedAnswer_thumbsUp', {
                          organizationUid,
                          customer,
                          sessionUid,
                        })
                        setReviewed(true)
                        toast({
                          title: t`Thank you for the feedback`,
                          description: t`You are helping us to improve handover answers`,
                          status: 'success',
                          isClosable: true,
                        })
                      }}
                      onDown={() => {
                        captureEvent('handover_recommendedAnswer_thumbsDown', {
                          organizationUid,
                          customer,
                          sessionUid,
                        })
                        setReviewed(true)
                        toast({
                          title: t`Thank you for the feedback`,
                          description: t`You are helping us to improve handover answers`,
                          status: 'success',
                          isClosable: true,
                        })
                      }}
                    />
                  </HStack>
                </Fade>
              </HStack>
              {answer.errorCode || answer.errorMessage ? (
                <Text fontSize="sm" color="red.600">
                  {answer.errorMessage || answer.errorCode}
                </Text>
              ) : answer.reply ? (
                <Box fontSize="sm" color="greys.900">
                  <Markdown
                    options={{
                      disableParsingRawHTML: true,
                      overrides: {
                        p: {
                          component: Text,
                          props: {
                            marginBottom: '2',
                          },
                        },
                        strong: {
                          component: chakra.span,
                          props: {
                            fontWeight: 'bold',
                          },
                        },
                        em: {
                          component: chakra.span,
                          props: {
                            fontStyle: 'italic',
                          },
                        },
                        ul: {
                          component: Box,
                          props: {
                            as: 'ul',
                            marginLeft: '4',
                          },
                        },
                        ol: {
                          component: Box,
                          props: {
                            as: 'ol',
                            marginLeft: '4',
                          },
                        },
                        li: {
                          component: Text,
                        },
                      },
                      renderRule: (next, node) => {
                        // Allow only subset of types
                        if (ALLOWED_RULES.has(node.type)) {
                          return next()
                        }
                        return ''
                      },
                    }}
                  >
                    {answer.reply}
                  </Markdown>
                </Box>
              ) : (
                <AnswerSkeleton />
              )}

              {/* Links */}
              {answer.reply == null && answer.errorMessage == null ? (
                <LinksSkeleton />
              ) : answer.links && answer.links.length > 0 ? (
                <Box>
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color="greys.300"
                    textTransform="uppercase"
                    mb="1"
                  >
                    <Trans>Matching articles</Trans>
                  </Text>
                  <Wrap spacing="2">
                    {answer.links.map((link) => (
                      <KnowledgeBaseLink key={link.id} link={link} />
                    ))}
                  </Wrap>
                </Box>
              ) : null}
            </VStack>
          </Box>
        </MagicBox>
      </Box>
    </VStack>
  )
}
